import css from './styles.module.css'

import { FaCheck as CheckIcon } from '@react-icons/all-files/fa/FaCheck'
import cn from 'clsx'
import * as React from 'react'
import type { Location } from 'react-router-dom'
import { Link } from 'react-router-dom'

import { Button as RadixButton, Flex, IconButton, Spinner } from 'components/ds'
import Icon, { IconKey } from 'components/ui/icon'
import Tooltip from 'components/ui/tooltip'

const getElement = (
  link: string | null | undefined,
  isRouterLink: boolean | null | undefined,
) => {
  if (link) {
    return isRouterLink ? Link : 'a'
  }

  return 'button'
}

export type ButtonAppearance =
  | 'subtle'
  | 'primary'
  | 'link'
  | 'danger'
  | 'icon'
  | 'inline-link'
  | 'help'
  | 'warning'
  | 'transparent'
  | 'black'
  | 'white'
  | 'yellow'

const appearanceToRadix = (
  appearance?: ButtonAppearance,
): React.ComponentProps<typeof IconButton> => {
  let variant: 'soft' | 'ghost' | 'solid' | 'surface' | 'classic' | 'outline' =
    'soft'
  let color: React.ComponentProps<typeof IconButton>['color'] = undefined
  let highContrast = false

  switch (appearance) {
    case 'primary':
      color = 'gray'
      variant = 'solid'
      highContrast = true
      break

    case 'subtle':
      color = 'gray'
      variant = 'soft'
      break

    case 'link':
      variant = 'ghost'
      break

    case 'inline-link':
      variant = 'ghost'
      break

    case 'danger':
      variant = 'soft'
      color = 'red'
      break

    case 'black':
      color = 'gray'
      highContrast = true
      break

    case 'warning':
      color = 'amber'
      variant = 'solid'
      break

    case 'yellow':
      color = 'yellow'
      variant = 'solid'
      highContrast = true
      break

    case 'icon':
      variant = 'ghost'
      color = 'gray'
      highContrast = true
      break

    case 'transparent':
      variant = 'soft'
      color = 'gray'
      highContrast = true
      break

    case 'help':
      variant = 'surface'
      break

    case 'white':
      variant = 'surface'
      color = 'gray'
      highContrast = true
      break

    default:
      variant = 'solid'
  }

  return {
    variant,
    color,
    highContrast,
  }
}

type Props = {
  appearance?: ButtonAppearance
  caption?: string | React.ReactNode
  compact?: boolean
  dataTestId?: string
  disabled?: boolean
  fullWidth?: boolean
  icon?: IconKey
  large?: boolean
  link?: string | Pick<Location, 'pathname' | 'state'>
  linkDownload?: boolean
  linkTarget?: string | null | undefined
  progressCaption?: string
  progressIndicator?: boolean
  rounded?: boolean
  split?: React.ReactNode
  tooltip?: string | React.ReactNode
  type?: React.ComponentProps<typeof RadixButton>['type']
  onClick?: (...args: Array<any>) => any
  onKeyDown?: (...args: Array<any>) => any
  textCenter?: boolean
  // TODO: Deprecate and clean
  iconNode?: React.ReactNode
  iconStyle?: Record<string, any>
  iconPosition?: 'left' | 'right'
  checked?: boolean
  checkedCaption?: string
  className?: string
  style?: Record<string, any>
  'aria-label'?: string
}
/**
 * A versatile button component with multiple appearances, link support, and icon capabilities
 *
 * @param appearance - Visual appearance style (default: 'subtle')
 * @param caption - Button text content
 * @param compact - Whether to use compact sizing
 * @param dataTestId - Test ID for testing purposes
 * @param disabled - Whether the button is disabled
 * @param fullWidth - Whether the button should take full width
 * @param icon - Icon key to display
 * @param large - Whether to use large sizing
 * @param link - URL or Location object for navigation
 * @param linkDownload - Whether the link should be downloaded
 * @param linkTarget - Target for the link
 * @param progressCaption - Caption to show during progress
 * @param progressIndicator - Whether to show progress indicator
 * @param rounded - Whether to use rounded corners
 * @param split - Content for split button
 * @param tooltip - Tooltip content
 * @param type - Button type attribute
 * @param onClick - Click handler function
 * @param onKeyDown - Key down handler function
 * @param textCenter - Whether to center text
 * @param iconNode - Custom icon React node (deprecated)
 * @param iconStyle - Custom icon styles (deprecated)
 * @param iconPosition - Position of the icon (default: 'left')
 * @param checked - Whether the button is checked
 * @param checkedCaption - Caption when checked
 * @param className - Additional CSS class name
 * @param style - Inline styles
 * @returns The button component
 */
const Button = React.forwardRef<HTMLButtonElement, Props>((props, ref) => {
  Button.displayName = 'Button'
  const {
    appearance = 'subtle',
    className,
    style,
    iconPosition = 'left',
  } = props

  const icon = props.iconNode ? (
    props.iconNode
  ) : props.icon ? (
    <Icon
      type={props.icon}
      style={props.iconStyle}
      width={props.compact ? '16' : '24'}
      height={props.compact ? '16' : '24'}
    />
  ) : null

  let link

  if (typeof props.link === 'string') {
    link = props.link
  } else if (props.link && typeof props.link === 'object') {
    link = props.link.pathname
  }

  let isRouterLink = false

  if (link) {
    if (props.linkDownload) {
      isRouterLink = false
    } else {
      // link type should be set explicitly, otherwise we guess
      isRouterLink = /^[/][^/]/.test(link)
    }
  }

  const RadixElement =
    !props.caption && (props.icon || props.iconNode) ? IconButton : RadixButton
  const Element = getElement(link, isRouterLink)
  const caption = props.progressIndicator ? (
    <>
      <Spinner />
      {props.progressCaption}…
    </>
  ) : props.checked ? (
    props.checkedCaption
  ) : (
    props.caption
  )
  const radixProps: React.ComponentProps<typeof IconButton> = {
    ...appearanceToRadix(appearance),
    radius: props.rounded ? 'full' : undefined,
    disabled: props.disabled || props.progressIndicator,
    size: props.compact ? '1' : props.large ? { xs: '2', lg: '3' } : undefined,
    onClick: props.onClick,
  }
  const button = (
    <RadixElement
      {...radixProps}
      className={cn(
        className,
        {
          [css.FullWidth]: props.fullWidth,
        },
        {
          [css.Inline]: appearance === 'inline-link',
        },
        {
          [css.ButtonSplit]: props.split,
        },
        {
          [css.Transparent]: props.appearance === 'transparent',
        },
        {
          [css.Black]: props.appearance === 'black',
        },
      )}
      asChild
      ref={ref}
      style={style}
      data-testid={props.dataTestId ? props.dataTestId : `button-${appearance}`}
    >
      {props.link && !props.disabled ? (
        isRouterLink ? (
          <Link
            to={
              typeof props.link === 'string' ? props.link : props.link.pathname
            }
            state={
              typeof props.link === 'object' ? props.link.state : undefined
            }
            aria-label={props['aria-label']}
          >
            {props.checked && <CheckIcon />}
            {iconPosition === 'left' ? icon : null}
            {caption}
            {iconPosition === 'right' ? icon : null}
          </Link>
        ) : (
          <a
            href={
              typeof props.link === 'string'
                ? props.link
                : JSON.stringify(props.link)
            }
            download={props.linkDownload}
            rel="noopener noreferrer"
            target={props.linkTarget ?? undefined}
            aria-label={props['aria-label']}
          >
            {props.checked && <CheckIcon />}
            {iconPosition === 'left' ? icon : null}
            {caption}
            {iconPosition === 'right' ? icon : null}
          </a>
        )
      ) : (
        <button type={props.type || 'button'} aria-label={props['aria-label']}>
          {props.checked && <CheckIcon />}
          {iconPosition === 'left' ? icon : null}
          {caption}
          {iconPosition === 'right' ? icon : null}
        </button>
      )}
    </RadixElement>
  )
  const wholeButton = props.split ? (
    <Flex className={css.SplitWrapper}>
      {button}
      <IconButton {...radixProps} className={css.ButtonRightSplit}>
        {props.split}
      </IconButton>
    </Flex>
  ) : (
    button
  )
  return props.tooltip ? (
    <Tooltip content={props.tooltip}>{wholeButton}</Tooltip>
  ) : (
    wholeButton
  )
})
export default React.memo(Button)
